import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Database,
  MessageSquare,
  LogOut,
  Users,
  ShoppingCart,
  FileCheck,
  Receipt,
  ReceiptText,
  FileSpreadsheet,
  Clock,
  CloudUpload,
  LayoutGrid,
  Gauge,
  Wrench,
  Search,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { agruparTools, filtrarTools } from '../lib/adminToolGroups';
import './AdminLayout.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Mapa nombre (lucide) → componente. El backend manda el nombre del ícono por
// herramienta; si no está mapeado, cae en un ícono genérico.
const ICON_MAP = {
  BarChart3,
  Database,
  MessageSquare,
  Users,
  ShoppingCart,
  FileCheck,
  Receipt,
  ReceiptText,
  FileSpreadsheet,
  Clock,
  CloudUpload,
  Gauge,
  Wrench
};

// Paleta SUR — Manual de Marca
// Azul principal: Pantone 299 C → #00a1df
// Gris corporativo: Pantone Cool Gray 8 C → #8a8a8d

// Qué grupos dejó minimizados el usuario. Es una comodidad de esta persona en
// este navegador, así que localStorage alcanza; si falla (modo privado, storage
// bloqueado) el menú arranca con todo desplegado y listo.
const CLAVE_COLAPSADOS = 'sur40.admin.grupos-colapsados';

const leerColapsados = () => {
  try {
    const crudo = window.localStorage.getItem(CLAVE_COLAPSADOS);
    const lista = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
};

const guardarColapsados = (lista) => {
  try {
    window.localStorage.setItem(CLAVE_COLAPSADOS, JSON.stringify(lista));
  } catch { /* silencioso: no vale romper el menú por no poder recordar esto */ }
};

// Ítem del menú. Vive fuera de AdminLayout porque se dibuja igual en la vista
// agrupada y en los resultados de búsqueda.
const ToolLink = ({ item }) => (
  <NavLink
    to={item.path}
    className={({ isActive }) => `
      flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 group relative
      ${isActive
        ? 'bg-[#00a1df]/10 text-[#00a1df] border border-[#00a1df]/20 shadow-[0_4px_20px_rgba(0,161,223,0.12)] ring-1 ring-[#00a1df]/10'
        : 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-100 border border-transparent'
      }
    `}
  >
    {({ isActive }) => (
      <>
        <item.icon className={`w-5 h-5 shrink-0 transition-all duration-300 ${isActive ? 'text-[#00a1df] scale-110' : 'text-gray-500 group-hover:text-gray-300 group-hover:scale-105'}`} />
        <div className="flex flex-col min-w-0">
          <span className={`text-sm font-bold tracking-tight transition-colors ${isActive ? 'text-white' : 'text-gray-400'}`}>
            {item.label}
          </span>
          <span className={`text-[10px] truncate transition-colors ${isActive ? 'text-[#00a1df]/55' : 'text-gray-600 group-hover:text-gray-500'}`}>
            {item.description}
          </span>
        </div>
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 bg-[#00a1df] rounded-r-full shadow-[0_0_10px_rgba(0,161,223,0.55)]" />
        )}
      </>
    )}
  </NavLink>
);

const AdminLayout = () => {
  const { user, token, logout, isAdmin, hasAdminTool, paneles } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);

  // El catálogo se trae desde el backend (Mongo). Sumar una herramienta nueva
  // hace que aparezca acá sola, sin tocar este archivo.
  const fetchCatalog = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/tool-catalog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      setCatalog(await resp.json());
    } catch { /* silencioso: sin catálogo el menú queda vacío */ }
  }, [token]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  // El super admin ve todo; un usuario con alcance solo sus herramientas asignadas.
  // "Cobranzas" ya viene como una sola entrada del catálogo (antes eran dos keys
  // que había que fusionar acá), así que no hace falta post-procesar nada.
  const menuItems = useMemo(() => catalog
    .filter((item) => hasAdminTool(item.key))
    .map((item) => ({
      key: item.key,
      path: `/admin/${item.key}`,
      label: item.label,
      description: item.description,
      // Se arrastran tal cual llegan del catálogo: los usan el agrupador y el
      // buscador (ver lib/adminToolGroups).
      group: item.group,
      group_label: item.group_label,
      group_order: item.group_order,
      keywords: item.keywords,
      icon: ICON_MAP[item.icon] || Wrench
    })), [catalog, hasAdminTool]);

  // ── Buscador del menú ──────────────────────────────────────────────────────
  const [busqueda, setBusqueda] = useState('');
  const inputBusqueda = useRef(null);
  const hayBusqueda = busqueda.trim() !== '';

  // Con búsqueda activa la lista se aplana: tres secciones de un ítem cada una
  // se leen peor que una lista corta y al hilo.
  const resultados = useMemo(
    () => (hayBusqueda ? filtrarTools(menuItems, busqueda) : []),
    [menuItems, busqueda, hayBusqueda]
  );
  const grupos = useMemo(() => agruparTools(menuItems), [menuItems]);

  // ── Grupos minimizados ─────────────────────────────────────────────────────
  const location = useLocation();
  const [colapsados, setColapsados] = useState(leerColapsados);

  const alternarGrupo = (key) => {
    setColapsados((previos) => {
      const siguiente = previos.includes(key)
        ? previos.filter((k) => k !== key)
        : [...previos, key];
      guardarColapsados(siguiente);
      return siguiente;
    });
  };

  // Ctrl/Cmd + K enfoca el buscador. Sin autofocus al montar: quien entra al
  // panel viene a trabajar, no a que le roben el foco.
  useEffect(() => {
    const alTeclear = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        inputBusqueda.current?.focus();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  return (
    <div className="flex h-screen bg-[#0F1117] text-white overflow-hidden font-sans">

      {/* Sidebar */}
      <aside className="w-72 bg-[#161922] border-r border-gray-800 flex flex-col shrink-0 z-20 shadow-2xl relative text-gray-300">

        {/* Barra de acento azul SUR en el tope del sidebar */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#00a1df] z-10" />

        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-800/50 bg-[#1A1D27]">
          <div className="flex items-center gap-3 mb-3">
            <img
              src="/icono-sur40.png"
              alt="Sur 4.0"
              className="w-10 h-10 rounded-lg shadow-inner border border-[#00a1df]/20"
            />
            <div>
              <h1 className="text-sm font-black tracking-widest uppercase text-white">Sur <span className="text-[#00a1df]">4.0</span></h1>
              <p className="text-[10px] text-[#00a1df]/60 font-bold tracking-tighter uppercase">Operaciones Inteligentes</p>
            </div>
          </div>
          {/* Tagline SUR */}
          <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#8a8a8d]/60 border-t border-gray-800/60 pt-3">
            Centro integral de higiene
          </p>
        </div>

        {/* Buscador de herramientas */}
        <div className="px-4 pt-4 pb-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
            <input
              ref={inputBusqueda}
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setBusqueda(''); e.currentTarget.blur(); }
              }}
              placeholder="Buscar herramienta..."
              aria-label="Buscar herramienta"
              data-testid="buscador-herramientas"
              className="w-full bg-[#0F1117] border border-gray-800 rounded-xl pl-9 pr-8 py-2 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#00a1df]/60 focus:ring-1 focus:ring-[#00a1df]/20 transition-colors [&::-webkit-search-cancel-button]:appearance-none"
            />
            {hayBusqueda && (
              <button
                type="button"
                onClick={() => { setBusqueda(''); inputBusqueda.current?.focus(); }}
                aria-label="Limpiar búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-600 hover:text-gray-200 hover:bg-gray-800 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 pt-2 space-y-1 overflow-y-auto custom-scrollbar">
          {hayBusqueda ? (
            resultados.length > 0 ? (
              <>
                <p className="px-3 text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-3">
                  {resultados.length} {resultados.length === 1 ? 'resultado' : 'resultados'}
                </p>
                {resultados.map((item) => <ToolLink key={item.path} item={item} />)}
              </>
            ) : (
              <div className="px-3 py-10 text-center">
                <Search className="w-5 h-5 text-gray-700 mx-auto mb-3" />
                <p className="text-xs font-bold text-gray-500">Sin resultados</p>
                <p className="text-[10px] text-gray-600 mt-1 break-words">
                  Ninguna herramienta coincide con «{busqueda.trim()}»
                </p>
              </div>
            )
          ) : (
            grupos.map((grupo) => {
              // El grupo "destacado" no tiene encabezado, así que tampoco se
              // minimiza: es el tablero de entrada y va siempre a la vista.
              const colapsado = Boolean(grupo.label) && colapsados.includes(grupo.key);
              const tieneActiva = grupo.tools.some((t) => t.path === location.pathname);
              return (
                <div key={grupo.key} className={grupo.label ? 'pt-4 first:pt-0' : ''}>
                  {grupo.label && (
                    <button
                      type="button"
                      onClick={() => alternarGrupo(grupo.key)}
                      aria-expanded={!colapsado}
                      aria-controls={`grupo-${grupo.key}`}
                      title={colapsado ? 'Mostrar las herramientas del grupo' : 'Minimizar el grupo'}
                      data-testid={`grupo-${grupo.key}`}
                      className="w-full flex items-center gap-1.5 px-3 mb-3 rounded-lg py-0.5 -my-0.5 hover:bg-gray-800/30 transition-colors group/head"
                    >
                      {colapsado
                        ? <ChevronRight className="w-3 h-3 shrink-0 text-gray-700 group-hover/head:text-gray-400 transition-colors" />
                        : <ChevronDown className="w-3 h-3 shrink-0 text-gray-700 group-hover/head:text-gray-400 transition-colors" />}
                      <span
                        className={`text-[10px] font-bold uppercase tracking-[0.2em] text-left transition-colors ${
                          colapsado && tieneActiva
                            ? 'text-[#00a1df]/80'
                            : 'text-gray-600 group-hover/head:text-gray-400'
                        }`}
                      >
                        {grupo.label}
                      </span>
                      {colapsado && (
                        <span className="ml-auto flex items-center gap-1.5 shrink-0">
                          {/* Minimizado y con la herramienta abierta adentro: el
                              punto evita tener que desplegarlo para ubicarse. */}
                          {tieneActiva && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00a1df] shadow-[0_0_6px_rgba(0,161,223,0.6)]" />
                          )}
                          <span className="text-[9px] font-black text-gray-700 group-hover/head:text-gray-500 transition-colors">
                            {grupo.tools.length}
                          </span>
                        </span>
                      )}
                    </button>
                  )}
                  {!colapsado && (
                    <div id={`grupo-${grupo.key}`} className="space-y-1">
                      {grupo.tools.map((item) => <ToolLink key={item.path} item={item} />)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* User Profile / Footer */}
        <div className="p-4 bg-[#1A1D27] border-t border-gray-800/50">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-900 border border-gray-800/50 mb-3 shadow-inner">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00a1df] to-[#006fa0] flex items-center justify-center text-white font-black shadow-lg border-2 border-[#00a1df]/25">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-white truncate leading-none mb-1">{user?.name}</p>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse" />
                <p className="text-[9px] text-gray-500 font-black uppercase tracking-tighter">{isAdmin ? 'Administrador del Sistema' : 'Acceso restringido'}</p>
              </div>
            </div>
          </div>
          {/* Solo si hay más de un panel al que volver. */}
          {paneles.length > 1 && (
            <button
              onClick={() => navigate('/')}
              data-testid="volver-inicio-btn"
              className="flex items-center justify-center gap-2 w-full py-2.5 mb-2 rounded-xl text-xs font-black text-[#00a1df]/70 hover:text-white hover:bg-[#00a1df] transition-all duration-300 border border-[#00a1df]/10 hover:border-[#00a1df] hover:shadow-[0_4px_20px_rgba(0,161,223,0.2)]"
            >
              <LayoutGrid className="w-4 h-4" />
              VOLVER AL INICIO
            </button>
          )}
          <button
            onClick={logout}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-black text-red-400/60 hover:text-white hover:bg-red-500 transition-all duration-300 border border-red-500/10 hover:border-red-500 hover:shadow-[0_4px_20px_rgba(239,68,68,0.2)]"
          >
            <LogOut className="w-4 h-4" />
            CERRAR SESIÓN
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 h-screen overflow-y-auto custom-scrollbar relative z-10 bg-[#0F1117]">
        {/* Glow decorativo — azul SUR muy sutil */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00a1df]/[0.025] blur-[160px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#8a8a8d]/[0.015] blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />

        {/* Content Wrapper */}
        <div className="relative p-10 min-h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
